import type { AnySceneObjectT } from "tweenly"
import { useMemo } from "react"
import { selectUndoable } from "../../helpers/selector.helpers"
import {
    DEFAULT_OBJECT_STYLES,
    ObjectStylesWithId,
    ObjectsById,
    multiSelectionWrapper,
} from "../../helpers/wrapper.helpers"
import { useAppSelector } from "../useRedux"

export type { ObjectStylesWithId }

/**
 * Multiselect wrapper geometry for 2+ selected objects.
 * Derived synchronously via useMemo — values never lag behind the selection.
 * `auto` dimensions are measured from the DOM; positions sum the full parent chain.
 */
const NO_SELECTION: string[] = []
const NO_OBJECTS: ObjectsById = {}

/**
 * @param enabled pass `false` from per-object call sites that never draw the multiselection
 * wrapper. The hook then subscribes to a constant instead of the selection array, so those
 * components stop re-rendering on every selection change. For a plain
 * "is a multiselection active?" check use `selectHasMultiSelection`, which is a boolean.
 */
const useWrapSelectedObjects = (enabled: boolean = true) => {
    const selectedObjectIds = useAppSelector((state) =>
        enabled ? state.activeObject.selected : NO_SELECTION
    )
    // The geometry only exists for 2+ selected objects, so anything else can skip the map
    // subscription entirely — otherwise the ~200 per-object call sites (useDraggable passes
    // `enabled: false`) re-render on every object change.
    const wrapperExist = selectedObjectIds.length > 1
    const objectsById = useAppSelector((state) =>
        wrapperExist ? (selectUndoable(state).objects.value as ObjectsById) : NO_OBJECTS
    )

    return useMemo(() => {
        if (!wrapperExist) {
            return {
                wrapperExist: false,
                wrapperObject: DEFAULT_OBJECT_STYLES,
                selectedObjects: [] as AnySceneObjectT[],
                allObjectsInWrapper: [] as ObjectStylesWithId[],
                activeChildObjectsIds: [] as string[],
            }
        }

        const selectedObjects = selectedObjectIds
            .map((id) => objectsById[id])
            .filter(Boolean) as AnySceneObjectT[]

        return {
            wrapperExist: true,
            selectedObjects,
            ...multiSelectionWrapper(selectedObjectIds, objectsById),
        }
    }, [wrapperExist, selectedObjectIds, objectsById])
}

export default useWrapSelectedObjects
